import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { empty, forkJoin, Observable, of, Subject, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccountDTO } from '../../../shared/account-dto';
import { CartDTO } from '../../../shared/cart-dto';
import { CheckoutDTO } from '../../../shared/checkout-dto';
import { CouponDTO } from '../../../shared/coupon-dto';
import { CreditDTO } from '../../../shared/credit-dto';
import { CustomerDTO } from '../../../shared/customer-dto';
import { CustomerOrderDTO } from '../../../shared/customer-order-dto';
import { PaymentMethodDTO } from '../../../shared/payment-method-dto';
import { ShipRateDTO } from '../../../shared/shipping-rate-dto';
import { DEFAULT_CHECKOUT_DTO, MIN_LOAD_TIME, STORAGE_CUSTOMER, STORAGE_CUSTOMER_JWT, STORAGE_REFRESH_CUSTOMER_DATE, STORAGE_TRACK } from './constants';
import { LoaderService } from './loader.service';
import { TrackingService } from './tracking.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  loggedIn: boolean;
  loginSubject = new Subject<any>();
  checkoutDTO = DEFAULT_CHECKOUT_DTO
  ref: string;
  noCookies = false;
  account: AccountDTO;

  checkoutSubject = new Subject<CheckoutDTO>();

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private trackingService: TrackingService,
  ) {
    this.checkoutSubject.subscribe(checkoutDTO => this.checkoutDTO = checkoutDTO)

    try {
      // Subscribe to login so I am always up to date
      this.loginSubject.subscribe(async loggedIn => {
        // Get the customer data
        const customer = JSON.parse(localStorage.getItem(STORAGE_CUSTOMER)) || this.checkoutDTO.customer
        this.updateCheckoutDTO({ customer })

        if (this.loggedIn != loggedIn) {
          // if changing login state, lets refresh the cart
          if (this.checkoutDTO.cart.cartKey && !location.pathname.includes('cart/checkout')) {
            (await this.fetchCart()).subscribe(x => null)
          } else if (!this.checkoutDTO.cart.cartKey) {
            (await this.fetchCart()).subscribe(x => null)
          }
          this.loggedIn = loggedIn;
        }
        this.loggedIn = loggedIn;
      })
    } catch(e) {
      this.noCookies = true;
      console.warn('Cookies disabled?', e)
    }
  }

  reportError(data) {
    this.http.post(`/api/payment/error-log`, { data })
    .subscribe(x => {
      console.log('posting message from UI to backend', x)
    })
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // ~~~~~~~~~~ LOGIN AUTH STUFF ~~~~~~~~~~
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  /**
   * Logs the user in given the email and sha1 password
   *
   * @param email email associated with the account
   * @param password user's password in sha1 form
   */
  login(email: string, password: string): Promise<any> {
    this.loaderService.show();

    try {
      localStorage.getItem('cart')
    } catch(e) {
      alert('Error logging in, make sure cookies are enabled')
      return;
    }

    let originalCartItemsLength = this.checkoutDTO.cart.items.length
    const oldCartKey = localStorage.getItem('cart')
    return new Promise(resolve => {
      this.http.post<any>('/api/auth/login', { email, password })
      .subscribe(res => {
        this.loaderService.hide();
        // login successful is there's a jwt token in the response
        if (res && res.token) {
          localStorage.setItem(STORAGE_CUSTOMER_JWT, res.token);
          localStorage.setItem(STORAGE_CUSTOMER, JSON.stringify(res.customer));
          localStorage.setItem(STORAGE_REFRESH_CUSTOMER_DATE, res.refresh_date);
          this.loginSubject.next(res.customer);

          // Google Analytics
          //this.trackingService.event('login');

          if (oldCartKey)
          {
            this.http.post<any>(`/api/cart/merge`, { oldCartKey })
            .subscribe(cart => {
              let merged = originalCartItemsLength && (cart.items.length != originalCartItemsLength)
              this.updateCheckoutDTO({ cart })
              return resolve({ merged })
            })
          }
          else
          {
            return resolve({ merged: false })
          }

        }
        else
        {
          this.loginSubject.next(false)
          return resolve('Invalid email and password')
        }
      }, error => {
        console.warn(error)
        this.loaderService.hide()
        return empty()
      })
    })
  }

  /**
   * Logs out the user and refreshes all browser storage
   */
  logout() {
    localStorage.removeItem(STORAGE_CUSTOMER_JWT)
    localStorage.removeItem(STORAGE_CUSTOMER)
    localStorage.removeItem(STORAGE_REFRESH_CUSTOMER_DATE)
    this.loginSubject.next(false)
    this.checkoutSubject.next(DEFAULT_CHECKOUT_DTO)
    return;
  }

  /**
   * Creates an account with the given email and pass
   * will throw an error if the email is already in use
   *
   * @param email email associated with new account
   * @param password sha1 password to use for the account
   * @param shipping optional parameter with customer's info
   */
  createAccount(email: string, password: string, shipping?: any, from?: any)
  {

    this.loaderService.show();

    // #1096 MERGE CART WHEN CREATING ACCOUNT
    let originalCartItemsLength = this.checkoutDTO.cart.items.length;
    const oldCartKey = localStorage.getItem('cart');

    return this.http.post(`/api/auth/create`, { email, password, shipping })
      .pipe(map((res: any) => {
        this.loaderService.hide();
        // Creating successful, jwt token in response
        if (res && res.token)
        {
          if (!shipping)
          {
            localStorage.setItem(STORAGE_CUSTOMER_JWT, res.token);
            localStorage.setItem(STORAGE_CUSTOMER, JSON.stringify(res.customer));
            localStorage.setItem(STORAGE_REFRESH_CUSTOMER_DATE, res.refresh_date);
            this.loginSubject.next(true);
          }

          // #1096 CALL MERGE CART API
          if (from && from == 'register') // PATH THAT COMES FROM frontend/src/app/shared/register/register.component.ts
          {
              console.log(`createAccount\n${originalCartItemsLength} items in ${oldCartKey}\n${from}\nMERGE with new cart\n${res.customer.id}`);

              if (oldCartKey)
              {

                this.http.post<any>(`/api/cart/merge`, { oldCartKey })
                .subscribe(cart => {
                  this.updateCheckoutDTO({ cart });
                });

              }
          }
          else                          // from == 'checkout'
          {
              console.log(`createAccount\n${originalCartItemsLength} items in ${oldCartKey}\n${from}\nNO MERGE, COMPLETE CHECKOUT\n${res.customer.id}`);
              // #1096 if it's not 'register', it's 'checkout', and we want to retain the current checkout flow without changing that code...
              // DO NOTHING DIFFERENT
          }


          return res.customer;
        }
        else
        {
          return 'Error creating account!'
        }
      }),

      catchError((e) => {
        this.loaderService.hide();
        throw e;
      })); // end .pipe
  }

  /**
   * Send a "change password" email to this account
   *
   * @param email email of the account to reset password
   */
  resetPasswordEmail(email: string): Observable<{ message: string }> {
    return this.http.post(`/api/auth/resetEmail`, { email }) as Observable<{ message: string }>
  }

  /**
   * Resets the password of the hash's account
   *
   * @param hash hash found in the email to reset the password
   * @param password New sha1(password) to be used for the account
   */
  changePassword(hash: string, password: string) {
    return this.http.post(`/api/auth/changePassword/`, { hash, password })
  }

  /**
   * Will update the loginSubject based on localstorage
   */
  isLoggedIn(): void {
    try {
      this.loginSubject.next(!!(localStorage.getItem(STORAGE_CUSTOMER_JWT)
        && localStorage.getItem(STORAGE_CUSTOMER)
        && localStorage.getItem(STORAGE_REFRESH_CUSTOMER_DATE)))
    } catch(e) {
      this.noCookies = true;
      console.warn('cookies disabled?', e)
    }
  }


  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // ~~~~~~~~~~~~~ CART STUFF ~~~~~~~~~~~~~
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  /**
   * Fetches the cart based on whether the user is logged in or not
   *
   * Will update the cart cookie if the user is NOT logged in
   */
  async fetchCart() {
    this.loaderService.show();
    return this.http.get<CartDTO>('/api/cart')
      .pipe(map(cart => {
        this.checkoutDTO.coupon = null;
        this.checkoutDTO.useCredits = false;
        this.checkoutDTO.payload = null;
        this.checkoutDTO.shippingRate1 = null;
        this.checkoutDTO.shippingRate2 = null;

        this.updateCheckoutDTO({ cart })
        this.loaderService.hide()
      }))
  }

  async emptyCart() {
    this.http.delete<CartDTO>(`/api/cart`)
    .subscribe(async x => {
      (await this.fetchCart()).subscribe(x => console.log(x))
    })
  }

  /**
   * Updates the cart with the given item
   *
   * @param param0 Object containing the products info to add to the cart
   */
  addItemToCart({
    bundleId,
    productInstanceIds,
    quantity,
  }: {
    bundleId?: number,
    productInstanceIds: number[],
    quantity?: number,
  }) {
    if (this.noCookies) {
      alert('Unable to access your cart, make sure cookies are enabled')
      return;
    }

    this.loaderService.show();
    const tracking = JSON.parse(localStorage.getItem(STORAGE_TRACK) || '{}');

    return forkJoin(
      timer(MIN_LOAD_TIME),
      this.http.post<CartDTO>(`/api/cart/addItem`, {
        productInstanceIds,
        bundleId,
        quantity,
        tracking
      })
    ).pipe(map(([a, cart]) => {
      this.updateCheckoutDTO({ cart })
      this.loaderService.hide();
      return cart;
    }), err => {
      return err;
    })
  }

  /**
   * Changes the quantity of the item supplied
   *
   * @param options Object containing the products info to add to the cart
   */
  changeQuantity(options: { cartItemId?: number, quantity: number, instanceId?: number }) {
    const tracking = JSON.parse(localStorage.getItem(STORAGE_TRACK) || '{}');
    if (options.quantity < 1) {
      options.quantity = 1;
    }
    return forkJoin(
        [
          timer(MIN_LOAD_TIME),
          this.http.post<CartDTO>(`/api/cart/changeQuantity`, { cartItemId: options.cartItemId, quantity: options.quantity, instanceId: options.instanceId, tracking })
        ]
    );
  }

  /**
   * Remove the product from the users cart
   *
   * @param param0 Object containing the products info to add to the cart
   */
  removeItemFromCart(options: { cartItemId?: number, instanceId?: number })
  {
    console.log("removeItemFromCart " + JSON.stringify(options));
    this.loaderService.show();
    const tracking = JSON.parse(localStorage.getItem(STORAGE_TRACK) || '{}');
    const originalCartItems = this.checkoutDTO.cart.items;

          return forkJoin([timer(MIN_LOAD_TIME), this.http.post<CartDTO>(`/api/cart/removeItem`, { cartItemId: options.cartItemId, instanceId: options.instanceId, tracking })])
                .pipe(map(([a, cart]) => {

                        this.loaderService.hide();
                        this.updateCheckoutDTO({ cart });

                        // Google Analytics
                        const newCartItemIds = this.checkoutDTO.cart.items.map(i => i.id);
                        const removedItem = originalCartItems.find(i => !newCartItemIds.includes(i.id));


                        // #1200 TO DO: this call doesn't actually seem to fire... we've always had SOME weirdness around removing from cart...
                        this.trackingService.event('remove_from_cart', this.account.id, {
                          value: removedItem.price,
                          currency: 'USD',
                          items: {
                            item_id: removedItem.instanceId,
                            item_name: removedItem.name,
                            item_brand: removedItem.artistName,
                            price: removedItem.price,
                            quantity: removedItem.quantity,
                            item_category: removedItem.format
                          },
                        });

                        return cart;
                      }
                    ), err => {
                        return err;
                      }
                  );
  }

  editOrder(orderId) {
    return this.http.post(`/api/cart/editOrder`, { orderId })
  }

  cancelOrder(orderId) {
    return this.http.delete(`/api/order/${orderId}`)
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // ~~~~~~~~~~~ CHECKOUT STUFF ~~~~~~~~~~~
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  /**
   * Returns ShipRateDTOs for the customer's location
   *
   * @param country country_iso of the customer
   * @param zip zip code of the customer
   */
  getShippingOptions(country: string, zip: string, separate?: boolean) {
    return this.http.post<[ShipRateDTO[], ShipRateDTO[]]>(`/api/shipping-rate/options`, { country, zip, separate })
  }

  /**
   * Returns OrderDTOs that this customer has used
   */
  getCustomerOrders() {
    return this.http.get<CustomerOrderDTO[]>(`/api/order/customer`)
  }

  /**
   * Returns PaymentMethodDTOs that this customer has saved (wholesale only)
   */
  getPaymentMethods() {
    return this.http.get<PaymentMethodDTO[]>(`/api/order-transaction/payment-methods`)
  }

  deletePaymentMethod(token) {
    return this.http.delete<PaymentMethodDTO[]>(`/api/order-transaction/payment-methods/${token}`)
  }

  /**
   * Checks if this email is associated with an account
   *
   * @param email email to check for an account
   */
  checkGuest(email: string) {
    return this.http.post<boolean>(`/api/cart/checkGuest`, { email })
  }

  /**
   * Adds this code to the customer's account if logged in
   * Adds the credit code to the guest's checkout process if not logged in
   *
   * @param code credit code string to redeem
   */
  redeemCredits(code: string): Observable<CreditDTO> {
    if (this.loggedIn) {
      return this.http.post(`/api/credit/redeem`, { code, checkoutDTO: this.checkoutDTO }) as Observable<CreditDTO>
    } else {
      return this.http.post(`/api/credit/redeem`, { code, checkoutDTO: this.checkoutDTO, email: this.checkoutDTO.customer.email }) as Observable<CreditDTO>
    }
  }

  /**
   * Gets the coupon (if any) associated with this code
   *
   * @param code coupon code string to redeem
   */
  redeemCouponCode(couponCode: string): Observable<[CouponDTO, CartDTO, number]> {
    if (!couponCode) {
      this.checkoutDTO.coupon = null;
      this.checkoutDTO.cart.couponCode = null;
      this.updateCheckoutDTO({ cart: this.checkoutDTO.cart})
    }
    return this.http.post<[CouponDTO, CartDTO, number]>(`/api/cart/redeem`, { couponCode, checkoutDTO: this.checkoutDTO, email: this.checkoutDTO.customer.email });
  }

  /**
   * If logged in this will fetch all credits from the customer's account
   */
  updateCredits() {
    if (this.loggedIn) {
      this.http.get(`/api/credit/customer`)
      .subscribe((creditDTOs: CreditDTO[]) => {
        this.updateCheckoutDTO({
          customer: {
            ...this.checkoutDTO.customer,
            credits: creditDTOs
          }
        })
      })
    }
  }

  /**
   * This emits a change to the checkoutSubject to keep all components in sync
   *
   * @param update The object to update inside the checkoutDTO
   */
  updateCheckoutDTO(update: {
    shippingRate1?: ShipRateDTO,
    shippingRate2?: ShipRateDTO,
    cart?: CartDTO,
    customer?: CustomerDTO,
    payload?: { nonce: string, deviceData: any },
    useCredits?: boolean,
  }) {
    const keys = Object.keys(update);
    if (keys.includes('payload')) {
      this.checkoutSubject.next({
        ref: this.ref,
        payload: update.payload,
        cart: this.checkoutDTO.cart,
        customer: this.checkoutDTO.customer,
        shippingRate1: this.checkoutDTO.shippingRate1,
        shippingRate2: this.checkoutDTO.shippingRate2,
        useCredits: this.checkoutDTO.useCredits,
        coupon: this.checkoutDTO.coupon,
        billingAddress: this.checkoutDTO.billingAddress,
      })
    }
    if (keys.includes('shippingRate1')) {
      this.checkoutSubject.next({
        ref: this.ref,
        cart: this.checkoutDTO.cart,
        payload: this.checkoutDTO.payload,
        customer: this.checkoutDTO.customer,
        shippingRate1: update.shippingRate1,
        shippingRate2: this.checkoutDTO.shippingRate2,
        useCredits: this.checkoutDTO.useCredits,
        billingAddress: this.checkoutDTO.billingAddress,
        coupon: this.checkoutDTO.coupon
      })
    }
    if (keys.includes('shippingRate2')) {
      this.checkoutSubject.next({
        ref: this.ref,
        cart: this.checkoutDTO.cart,
        payload: this.checkoutDTO.payload,
        customer: this.checkoutDTO.customer,
        shippingRate1: this.checkoutDTO.shippingRate1,
        shippingRate2: update.shippingRate2,
        useCredits: this.checkoutDTO.useCredits,
        billingAddress: this.checkoutDTO.billingAddress,
        coupon: this.checkoutDTO.coupon
      })
    }

    if (keys.includes('cart')) {
      this.checkoutSubject.next({
        ref: this.ref,
        cart: update.cart,
        payload: this.checkoutDTO.payload,
        customer: this.checkoutDTO.customer,
        shippingRate1: this.checkoutDTO.shippingRate1,
        shippingRate2: this.checkoutDTO.shippingRate2,
        useCredits: this.checkoutDTO.useCredits,
        billingAddress: this.checkoutDTO.billingAddress,
        coupon: this.checkoutDTO.coupon
      })
    }

    if (keys.includes('customer')) {
      this.checkoutSubject.next({
        ref: this.ref,
        cart: this.checkoutDTO.cart,
        payload: this.checkoutDTO.payload,
        shippingRate1: this.checkoutDTO.shippingRate1,
        shippingRate2: this.checkoutDTO.shippingRate2,
        billingAddress: this.checkoutDTO.billingAddress,
        customer: {
          credits: this.checkoutDTO.customer.credits,
          ...update.customer,
        },
        useCredits: this.checkoutDTO.useCredits,
        coupon: this.checkoutDTO.coupon
      })
    }

    if (keys.includes('useCredits')) {
      this.checkoutSubject.next({
        ref: this.ref,
        cart: this.checkoutDTO.cart,
        payload: this.checkoutDTO.payload,
        shippingRate1: this.checkoutDTO.shippingRate1,
        shippingRate2: this.checkoutDTO.shippingRate2,
        customer: this.checkoutDTO.customer,
        billingAddress: this.checkoutDTO.billingAddress,
        useCredits: update.useCredits,
        coupon: this.checkoutDTO.coupon
      })
    }
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // ~~~~~~~~~~~ ADDRESS STUFF ~~~~~~~~~~~~
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  getAddresses(): Observable<any[]> {
    if (!this.loggedIn) {
      return of([]);
    } else {
      return this.http.get<any[]>(`/api/address`)
    }
  }

  updateAddress(address: any): Observable<any[]> {
    if (!this.loggedIn) {
      return of([]);
    } else {
      return this.http.post<any[]>(`/api/address/update/${address.id}`, { address })
    }
  }

  addAddress(address: any): Observable<any[]> {
    if (!this.loggedIn) {
      return of([]);
    } else {
      return this.http.post<any[]>(`/api/address/add`, { address })
    }
  }

  deleteAddress(addressId: number): Observable<any[]> {
    if (!this.loggedIn) {
      return of([]);
    } else {
      return this.http.delete<any[]>(`/api/address/${addressId}`)
    }
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // ~~~~~~~~~~~ SHIPPING STUFF ~~~~~~~~~~~~
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  getFreeShipping(): Observable<{ free, other }> {
    return this.http.get<{ free, other }>(`/api/shipping-rate/free-shipping`)
  }

  emailSignup(email, method): Observable<any> {
    return this.http.post(`/api/code-batch/emailer`, { email, method })
  }

  validateCart(ship_country_iso) {
    return this.http.post(`/api/productInstance/validate`, { ship_country_iso })
  }

}